import { useCallback, useEffect, useRef } from 'react';

import { getHasInterestCreditValue } from 'app/pages/loanApplication/tabs/loanClosingCDS/shared/ClosingInformation/ClosingInformation.utils';
import dayjs from 'dayjs';
import _ from 'lodash';

import {
  checkIsChattelLoanApplication,
  checkIsChattelRescindable,
  checkIsDefaultDisbursementForFundingDate,
  checkIsHelocLoanApplication,
  checkIsSameDisbursementForNewFundingDate,
  getChattelFundingDate,
  getDefaultDisbursementDate,
  getDefaultInterestCreditFirstPaymentDate,
  getFundingDateValueByCreditNoteDate,
  getNextBusinessDay,
  getRescisionDateValue,
} from 'utils/loanPrepaidsDates';

import { SECTION_HREF_MAP } from '../../EditDrawer.config';
import { ClosingFundingDatesFormFieldId } from '../Content.constants';
import { getMaturityDateByFirstPaymentDate, getTimestamp } from '../Content.utils';

export const useDateCascade = ({
  form,
  setInForm,
  currentSection,
  hasInterestCredit,
  businessDays,
  loanApplication,
  subjectPropertyOccupancy,
  subjectPropertyState,
  setCurrentCloseAt,
  setCurrentNoteDate,
  setCurrentFundingDate = _.noop,
  maturityTermMonths,
  isChattel: isChattelFromLoanInfo = false,
}) => {
  // Loan info and common info load independently; either one marks the loan chattel (LEN-198).
  const isChattel = isChattelFromLoanInfo || checkIsChattelLoanApplication(loanApplication);
  const formNoteDate = form[ClosingFundingDatesFormFieldId.INTEREST_CREDIT_NOTE_DATE];
  const formFundingDate = form[ClosingFundingDatesFormFieldId.INTEREST_CREDIT_FUNDING_DATE];
  const formDisbursementDate =
    form[ClosingFundingDatesFormFieldId.INTEREST_CREDIT_DISBURSEMENT_DATE];
  const formRescissionDate = form[ClosingFundingDatesFormFieldId.INTEREST_CREDIT_RESCISSION_DATE];
  const formFirstPaymentDate =
    form[ClosingFundingDatesFormFieldId.INTEREST_CREDIT_FIRST_PAYMENT_DATE];

  const isRescissionFromCascadeRef = useRef(false);

  const recalculateFirstPaymentDate = useCallback(
    fundingDate => {
      const disbursementDate = getDefaultDisbursementDate(fundingDate, businessDays);
      const firstPaymentDate = getDefaultInterestCreditFirstPaymentDate({
        hasInterestCredit: getHasInterestCreditValue({
          fundingDate: disbursementDate,
          hasInterestCredit,
          loanApplication,
          subjectPropertyState,
        }),
        interestCreditFundingDate: disbursementDate,
        isHeloc: checkIsHelocLoanApplication(loanApplication),
        isChattel,
      });

      setInForm(
        ClosingFundingDatesFormFieldId.INTEREST_CREDIT_FIRST_PAYMENT_DATE,
        firstPaymentDate,
        false,
      );
    },
    [businessDays, hasInterestCredit, loanApplication, subjectPropertyState, isChattel, setInForm],
  );

  // The closing dates can arrive before the loan's common info or loan info, and every date moving
  // from empty to its stored value would cascade as if edited -- with the retail rules, since the
  // loan is not yet known to be chattel. Until the loan is known the baselines still follow the
  // loaded dates and the closing date still syncs, but no date cascades, so the stored dates are
  // kept (LEN-198).
  const isLoanKnown =
    isChattel ||
    !_.isNil(loanApplication?.application?.loanPurpose) ||
    !_.isNil(loanApplication?.application?.loanType);

  // Read through refs so the cascades keep a stable identity while the dates are edited.
  const formFundingDateRef = useRef(formFundingDate);
  const formFirstPaymentDateRef = useRef(formFirstPaymentDate);

  useEffect(() => {
    formFundingDateRef.current = formFundingDate;
    formFirstPaymentDateRef.current = formFirstPaymentDate;
  }, [formFundingDate, formFirstPaymentDate]);

  // The stored dates arrive after the first render, so on load each date moves from empty to its
  // stored value and would cascade as if the user had edited it — before Purpose of Chattel has
  // loaded. A chattel loan keeps its stored (possibly typed) dates on load (LEN-198).
  const checkIsChattelLoad = useCallback(
    prevTimestamp => isChattel && _.isNull(prevTimestamp) && !_.isNil(formFundingDateRef.current),
    [isChattel],
  );

  // Likewise a stored chattel first payment date still after funding is kept rather than
  // re-defaulted from the funding date that just loaded.
  const checkIsChattelDateLoad = useCallback(
    (prevFundingTimestamp, fundingTimestamp) =>
      isChattel &&
      _.isNull(prevFundingTimestamp) &&
      !_.isNil(formFirstPaymentDateRef.current) &&
      dayjs(formFirstPaymentDateRef.current).isAfter(fundingTimestamp, 'day'),
    [isChattel],
  );

  // Chattel loans fund weeks after docs (LEN-198): a funding date the user typed on or after the
  // earliest allowed date is kept, and the first payment date only follows a funding date that moved.
  const setChattelFundingDate = useCallback(
    floorDate => {
      const currentFundingDate = formFundingDateRef.current;
      const fundingDate = getChattelFundingDate({ currentFundingDate, floorDate });

      if (getTimestamp(fundingDate) === getTimestamp(currentFundingDate)) {
        return;
      }

      setInForm(
        ClosingFundingDatesFormFieldId.INTEREST_CREDIT_DISBURSEMENT_DATE,
        getDefaultDisbursementDate(fundingDate, businessDays),
        false,
      );
      setInForm(ClosingFundingDatesFormFieldId.INTEREST_CREDIT_FUNDING_DATE, fundingDate, false);
      recalculateFirstPaymentDate(fundingDate);
    },
    [businessDays, recalculateFirstPaymentDate, setInForm],
  );

  const calculateAndSetDates = useCallback(
    noteDate => {
      const rescissionDate = getRescisionDateValue({
        businessDays,
        loanApplication,
        interestCreditNoteDate: noteDate,
        subjectPropertyOccupancy,
      });
      const fundingDate = rescissionDate
        ? getNextBusinessDay({ date: rescissionDate, businessDays })
        : getFundingDateValueByCreditNoteDate({
            businessDays,
            loanApplication,
            interestCreditNoteDate: noteDate,
            subjectPropertyOccupancy,
          });
      isRescissionFromCascadeRef.current = !!rescissionDate;
      setInForm(
        ClosingFundingDatesFormFieldId.INTEREST_CREDIT_RESCISSION_DATE,
        rescissionDate,
        false,
      );

      if (isChattel) {
        setChattelFundingDate(fundingDate);

        return;
      }

      setInForm(
        ClosingFundingDatesFormFieldId.INTEREST_CREDIT_DISBURSEMENT_DATE,
        getDefaultDisbursementDate(fundingDate, businessDays),
        false,
      );
      setInForm(ClosingFundingDatesFormFieldId.INTEREST_CREDIT_FUNDING_DATE, fundingDate, false);
      recalculateFirstPaymentDate(fundingDate);
    },
    [
      businessDays,
      loanApplication,
      subjectPropertyOccupancy,
      isChattel,
      recalculateFirstPaymentDate,
      setChattelFundingDate,
      setInForm,
    ],
  );

  const emptyDates = useCallback(() => {
    setInForm(ClosingFundingDatesFormFieldId.INTEREST_CREDIT_RESCISSION_DATE, null, false);
    setInForm(ClosingFundingDatesFormFieldId.INTEREST_CREDIT_FUNDING_DATE, null, false);
    setInForm(ClosingFundingDatesFormFieldId.INTEREST_CREDIT_DISBURSEMENT_DATE, null, false);
    setInForm(ClosingFundingDatesFormFieldId.INTEREST_CREDIT_FIRST_PAYMENT_DATE, null, false);
  }, [setInForm]);

  const prevCloseAtRef = useRef();

  useEffect(() => {
    if (currentSection !== SECTION_HREF_MAP.SCHEDULING) {
      return;
    }

    const currentTimestamp = getTimestamp(form.closeAt);
    const prev = prevCloseAtRef.current;
    prevCloseAtRef.current = currentTimestamp;

    if (_.isUndefined(prev)) {
      return;
    }

    if (currentTimestamp && currentTimestamp !== prev) {
      setInForm(
        ClosingFundingDatesFormFieldId.INTEREST_CREDIT_NOTE_DATE,
        dayjs(form.closeAt),
        false,
      );

      if (isLoanKnown && !checkIsChattelLoad(prev)) {
        calculateAndSetDates(dayjs(form.closeAt));
      }
    }

    if (!currentTimestamp && prev) {
      setInForm('location', null, false);
      setInForm(ClosingFundingDatesFormFieldId.INTEREST_CREDIT_NOTE_DATE, null, false);
      emptyDates();
    }
  }, [
    form.closeAt,
    calculateAndSetDates,
    currentSection,
    setInForm,
    emptyDates,
    checkIsChattelLoad,
    isLoanKnown,
  ]);

  const prevNoteDateRef = useRef();

  useEffect(() => {
    if (currentSection !== SECTION_HREF_MAP.CLOSING_FUNDING_DATES) {
      return;
    }

    const currentTimestamp = getTimestamp(formNoteDate);
    const prev = prevNoteDateRef.current;
    prevNoteDateRef.current = currentTimestamp;

    if (_.isUndefined(prev)) {
      return;
    }

    if (currentTimestamp && currentTimestamp !== prev) {
      setInForm('closeAt', dayjs(formNoteDate), false);
      setCurrentCloseAt(currentTimestamp);

      if (isLoanKnown && !checkIsChattelLoad(prev)) {
        calculateAndSetDates(dayjs(formNoteDate));
      }
    }

    if (!currentTimestamp && prev) {
      setInForm('closeAt', null, false);
      setInForm('location', null, false);
      setCurrentCloseAt(null);
      emptyDates();
    }
  }, [
    formNoteDate,
    calculateAndSetDates,
    currentSection,
    emptyDates,
    setInForm,
    setCurrentCloseAt,
    checkIsChattelLoad,
    isLoanKnown,
  ]);

  const prevRescissionDateRef = useRef();

  useEffect(() => {
    if (currentSection !== SECTION_HREF_MAP.CLOSING_FUNDING_DATES) {
      return;
    }

    const currentTimestamp = getTimestamp(formRescissionDate);
    const prev = prevRescissionDateRef.current;
    prevRescissionDateRef.current = currentTimestamp;

    if (_.isUndefined(prev)) {
      isRescissionFromCascadeRef.current = false;

      return;
    }

    if (isRescissionFromCascadeRef.current) {
      isRescissionFromCascadeRef.current = false;

      return;
    }

    if (isLoanKnown && currentTimestamp && currentTimestamp !== prev) {
      const fundingDate = getNextBusinessDay({
        date: dayjs(formRescissionDate),
        businessDays,
      });

      // A chattel rescission only bounds funding when the user edits it on a rescindable loan; the
      // stored value arriving on load must not move a typed funding date (LEN-198).
      if (isChattel) {
        if (!checkIsChattelLoad(prev) && checkIsChattelRescindable(loanApplication)) {
          setChattelFundingDate(fundingDate);
        }

        return;
      }

      setInForm(ClosingFundingDatesFormFieldId.INTEREST_CREDIT_FUNDING_DATE, fundingDate, false);
      setInForm(
        ClosingFundingDatesFormFieldId.INTEREST_CREDIT_DISBURSEMENT_DATE,
        getDefaultDisbursementDate(fundingDate, businessDays),
        false,
      );
      recalculateFirstPaymentDate(fundingDate);
    }
  }, [
    formRescissionDate,
    currentSection,
    businessDays,
    isChattel,
    loanApplication,
    setInForm,
    recalculateFirstPaymentDate,
    setChattelFundingDate,
    checkIsChattelLoad,
    isLoanKnown,
  ]);

  const prevFundingDateRef = useRef();

  useEffect(() => {
    if (currentSection !== SECTION_HREF_MAP.CLOSING_FUNDING_DATES) {
      return;
    }

    const currentTimestamp = getTimestamp(formFundingDate);
    const prev = prevFundingDateRef.current;
    prevFundingDateRef.current = currentTimestamp;

    if (_.isUndefined(prev)) {
      return;
    }

    if (
      currentTimestamp &&
      currentTimestamp !== prev &&
      isLoanKnown &&
      !checkIsChattelDateLoad(prev, currentTimestamp)
    ) {
      const fundingDate = dayjs(formFundingDate);

      setInForm(
        ClosingFundingDatesFormFieldId.INTEREST_CREDIT_DISBURSEMENT_DATE,
        getDefaultDisbursementDate(fundingDate, businessDays),
        false,
      );

      if (
        !checkIsSameDisbursementForNewFundingDate({
          prevFundingDate: prev,
          fundingDate,
          businessDays,
        })
      ) {
        recalculateFirstPaymentDate(fundingDate);
      }
    }
  }, [
    formFundingDate,
    currentSection,
    businessDays,
    setInForm,
    recalculateFirstPaymentDate,
    checkIsChattelDateLoad,
    isLoanKnown,
  ]);

  const prevDisbursementDateRef = useRef();

  useEffect(() => {
    if (currentSection !== SECTION_HREF_MAP.CLOSING_FUNDING_DATES) {
      return;
    }

    const currentTimestamp = getTimestamp(formDisbursementDate);
    const prev = prevDisbursementDateRef.current;
    prevDisbursementDateRef.current = currentTimestamp;

    if (_.isUndefined(prev)) {
      return;
    }

    const isDefaultForFundingDate = checkIsDefaultDisbursementForFundingDate({
      fundingDate: formFundingDateRef.current,
      disbursementDate: dayjs(formDisbursementDate),
      businessDays,
    });

    if (
      currentTimestamp &&
      currentTimestamp !== prev &&
      isLoanKnown &&
      !isDefaultForFundingDate &&
      !checkIsChattelDateLoad(prev, currentTimestamp)
    ) {
      setInForm(
        ClosingFundingDatesFormFieldId.INTEREST_CREDIT_FUNDING_DATE,
        dayjs(formDisbursementDate),
        false,
      );
      recalculateFirstPaymentDate(dayjs(formDisbursementDate));
    }
  }, [
    formDisbursementDate,
    currentSection,
    businessDays,
    setInForm,
    recalculateFirstPaymentDate,
    checkIsChattelDateLoad,
    isLoanKnown,
  ]);

  const prevFirstPaymentDateRef = useRef();

  // Maturity Date is the due date of the final scheduled payment, so it is derived from
  // whatever first payment date the form currently holds — whether a cascade wrote it or
  // the user picked it directly. Owning the projection here (rather than in each cascade)
  // keeps the two from drifting apart, which is the defect this fix removes. Not gated on
  // a section: the note-date cascade can be driven from Scheduling. An unreconstructable
  // term clears the field rather than leaving a date derived from the previous schedule.
  useEffect(() => {
    const currentTimestamp = getTimestamp(formFirstPaymentDate);
    const prev = prevFirstPaymentDateRef.current;
    prevFirstPaymentDateRef.current = currentTimestamp;

    if (_.isUndefined(prev) || currentTimestamp === prev) {
      return;
    }

    setInForm(
      ClosingFundingDatesFormFieldId.MATURITY_DATE,
      getMaturityDateByFirstPaymentDate(formFirstPaymentDate, maturityTermMonths),
      false,
    );
  }, [formFirstPaymentDate, maturityTermMonths, setInForm]);

  useEffect(() => {
    if (currentSection !== SECTION_HREF_MAP.SCHEDULING) {
      return;
    }

    setCurrentCloseAt(getTimestamp(form.closeAt));
  }, [form.closeAt, currentSection, setCurrentCloseAt]);

  useEffect(() => {
    if (currentSection !== SECTION_HREF_MAP.CLOSING_FUNDING_DATES) {
      return;
    }

    setCurrentNoteDate(getTimestamp(formNoteDate));
  }, [formNoteDate, currentSection, setCurrentNoteDate]);

  useEffect(() => {
    setCurrentFundingDate(getTimestamp(formFundingDate));
  }, [formFundingDate, setCurrentFundingDate]);
};
